import { useCallback, useState } from 'react';
import { audioHistoryService } from '../services/sessionService';
import type { UpdateLastPlayedResponse } from '../types/sessions/service/types';

type SessionKind = 'v' | 'b';

interface UseUpdateLastPlayedState {
  loading: boolean;
  error: string | null;
  data: UpdateLastPlayedResponse | null;
}

interface UseUpdateLastPlayedReturn extends UseUpdateLastPlayedState {
  updateLastPlayed: (sessionId: number, type: SessionKind) => Promise<UpdateLastPlayedResponse | null>;
  updateVisualization: (sessionId: number) => Promise<UpdateLastPlayedResponse | null>;
  updateBooks: (sessionId: number) => Promise<UpdateLastPlayedResponse | null>;
  reset: () => void;
}

const initialState: UseUpdateLastPlayedState = {
  loading: false,
  error: null,
  data: null,
};

export const useUpdateLastPlayed = (): UseUpdateLastPlayedReturn => {
  const [state, setState] = useState<UseUpdateLastPlayedState>(initialState);

  const updateLastPlayed = useCallback(async (sessionId: number, type: SessionKind) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await audioHistoryService.updateLastPlayed(sessionId, type);
      setState(prev => ({ ...prev, data: result }));
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update last played';
      setState(prev => ({ ...prev, error: message }));
      return null;
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const updateVisualization = useCallback(async (sessionId: number) => {
    return updateLastPlayed(sessionId, 'v');
  }, [updateLastPlayed]);

  const updateBooks = useCallback(async (sessionId: number) => {
    return updateLastPlayed(sessionId, 'b');
  }, [updateLastPlayed]);

  const reset = useCallback(() => setState(initialState), []);

  return {
    ...state,
    updateLastPlayed,
    updateVisualization,
    updateBooks,
    reset,
  };
};

